import { useEffect, useState } from "react";
import { Plus, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { blobUrl } from "@/lib/scanOptions";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const PKGS = { STANDARD: "Standart", SILVER: "Gümüş", GOLD: "Gold" };
const today = () => new Date().toISOString().slice(0, 10);

const AdThumb = ({ ad }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => { if (ad.image_type) blobUrl(`/ads/${ad.id}/image`).then(setSrc).catch(() => {}); }, [ad.id, ad.image_type]);
  return <div className="w-28 h-16 rounded bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">{src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-slate-300" />}</div>;
};

const AdDialog = ({ open, onClose, onSaved }) => {
  const [f, setF] = useState({});
  const [file, setFile] = useState(null);
  useEffect(() => { if (open) { setF({ title: "", link_url: "", packages: Object.keys(PKGS), start_date: today(), end_date: today(), active: true }); setFile(null); } }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Reklam görseli seçin");
    try {
      const { data } = await api.post("/admin/ads", { ...f, link_url: f.link_url || null });
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/ads/${data.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Reklam yayına alındı");
      onSaved();
      onClose();
    } catch (x) { toast.error(formatApiError(x.response?.data?.detail)); }
  };
  const toggle = (p) => setF({ ...f, packages: f.packages.includes(p) ? f.packages.filter((x) => x !== p) : [...f.packages, p] });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ad-dialog">
        <DialogHeader><DialogTitle>Yeni pop-up reklam</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Başlık *</Label><Input data-testid="ad-title" required minLength={2} value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
          <div><Label>Bağlantı (isteğe bağlı)</Label><Input data-testid="ad-link" placeholder="https://" value={f.link_url || ""} onChange={(e) => setF({ ...f, link_url: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Başlangıç</Label><Input data-testid="ad-start" type="date" value={f.start_date || ""} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
            <div><Label>Bitiş</Label><Input data-testid="ad-end" type="date" value={f.end_date || ""} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></div>
          </div>
          <div><Label>Gösterilecek paketler</Label>
            <div className="flex gap-2 mt-1">{Object.entries(PKGS).map(([k, l]) => (
              <button type="button" key={k} data-testid={`ad-pkg-${k}`} onClick={() => toggle(k)} className={`px-3 py-1.5 rounded-full text-xs border ${f.packages?.includes(k) ? "bg-slate-900 text-white" : "bg-white"}`}>{l}</button>
            ))}</div></div>
          <div><Label>Görsel *</Label><Input data-testid="ad-image" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} /></div>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose}>İptal</Button><Button data-testid="ad-save-btn" type="submit" className="bg-slate-900"><Upload className="w-4 h-4 mr-1" />Yayınla</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const AdsPanel = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/ads").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (a) => { await api.put(`/admin/ads/${a.id}`, { ...a, active: !a.active }); load(); };
  const del = async (a) => { if (window.confirm("Reklam silinsin mi?")) { await api.delete(`/admin/ads/${a.id}`); load(); } };
  return (
    <div className="space-y-3" data-testid="ads-panel">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Pop-up görsel reklamlar</h2>
          <p className="text-sm text-slate-500">Ofis kullanıcılarına pop-up olarak gösterilir. Günlük gösterim sayısı ve tetikleyiciler: Ayarlar &gt; Reklam gösterimi.</p>
        </div>
        <Button data-testid="add-ad-btn" className="bg-slate-900" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" />Yeni reklam</Button>
      </div>
      <Card className="divide-y">
        {items.length === 0 && <div className="p-8 text-center text-sm text-slate-400">Henüz reklam yok</div>}
        {items.map((a) => (
          <div key={a.id} data-testid={`ad-row-${a.id}`} className="p-3 flex items-center gap-3">
            <AdThumb ad={a} />
            <div className="flex-1 min-w-0">
              <div className="font-medium">{a.title}</div>
              <div className="text-xs text-slate-500">{formatDate(a.start_date)} – {formatDate(a.end_date)} · {a.views} gösterim</div>
              <div className="flex gap-1 mt-1">{a.packages.map((p) => <Badge key={p} variant="outline" className="text-[10px]">{PKGS[p]}</Badge>)}</div>
            </div>
            <Switch data-testid={`ad-active-${a.id}`} checked={a.active} onCheckedChange={() => toggle(a)} />
            <Button size="icon" variant="ghost" data-testid={`ad-delete-${a.id}`} onClick={() => del(a)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
          </div>
        ))}
      </Card>
      <AdDialog open={open} onClose={() => setOpen(false)} onSaved={load} />
    </div>
  );
};
